import { StringContext } from "../../core/StringContext";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";

interface LazyImageState {
  src: string;
  aspectReady: boolean;
  contentReady: boolean;
  aspectLoading: boolean;
  contentLoading: boolean;
  pendingActivation: boolean;
  width?: number;
  height?: number;
  aspectPromise?: Promise<void>;
  unsubscribe?: () => void;
}

/**
 * Module that handles lazy-loading of images with `string-lazy` attribute.
 * Aspect ratios are precomputed before the real source is revealed in view.
 */
export class StringLazy extends StringModule {
  private isStartLoaded = false;
  private loadingCount = 0;
  private imageStates = new WeakMap<HTMLImageElement, LazyImageState>();

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "lazy";
  }

  onInit(): void {
    const images = document.querySelectorAll("img[string-lazy], img[data-string-lazy]");
    images.forEach((img) => this.prepareAspectRatio(img as HTMLImageElement));
    this.isStartLoaded = true;
  }

  onObjectConnected(object: StringObject): void {
    const element = object.htmlElement;
    if (!(element instanceof HTMLImageElement)) return;

    const state = this.ensureState(element);
    this.prepareAspectRatio(element);

    const listener = (data: { inView: boolean } | undefined) => {
      this.handleInView(element, state, !!data?.inView);
    };

    if (state.unsubscribe) {
      state.unsubscribe();
    }
    this.events.on(`object:inview:${object.id}`, listener);
    state.unsubscribe = () => this.events.off(`object:inview:${object.id}`, listener);

    if (this.isStartLoaded && (object.getProperty<boolean>("is-inview") ?? false)) {
      this.handleInView(element, state, true);
    }
  }

  onObjectDisconnected(object: StringObject): void {
    const element = object.htmlElement;
    if (!(element instanceof HTMLImageElement)) return;
    const state = this.imageStates.get(element);
    if (!state) return;

    state.pendingActivation = false;
    if (state.unsubscribe) {
      state.unsubscribe();
      state.unsubscribe = undefined;
    }
  }

  private ensureState(img: HTMLImageElement): LazyImageState {
    let state = this.imageStates.get(img);
    if (!state) {
      const src = this.readSource(img);
      state = {
        src,
        aspectReady: false,
        contentReady: false,
        aspectLoading: false,
        contentLoading: false,
        pendingActivation: false,
      };
      this.imageStates.set(img, state);

      if (!img.classList.contains("lazyLoad")) {
        img.classList.add("lazyLoad");
      }
      if (img.dataset && !img.dataset.stringLazySrc && src) {
        img.dataset.stringLazySrc = src;
      }
      return state;
    }

    if (!state.src) {
      state.src = this.readSource(img);
    }
    return state;
  }

  private readSource(img: HTMLImageElement): string {
    const raw = this.tools.domAttribute.process({
      element: img,
      key: this.htmlKey,
      fallback: "",
    });
    if (typeof raw === "string") {
      return raw;
    }
    if (raw == null) {
      return "";
    }
    return String(raw);
  }

  private prepareAspectRatio(img: HTMLImageElement): void {
    const state = this.ensureState(img);
    if (!state.src || state.aspectLoading || state.aspectReady) {
      return;
    }

    state.aspectLoading = true;
    const crossOrigin = img.getAttribute("crossorigin");
    const referrerPolicy = img.getAttribute("referrerpolicy");

    state.aspectPromise = this.loadIntrinsicSize(state.src, crossOrigin, referrerPolicy)
      .then(({ width, height }) => {
        if (width > 0 && height > 0) {
          img.style.aspectRatio = `${width} / ${height}`;
          state.width = width;
          state.height = height;
        }
        state.aspectReady = true;
        state.aspectLoading = false;
        this.maybeActivateImage(img, state);
      })
      .catch((error) => {
        console.warn("Failed to read image size for lazy image:", state.src, error);
        state.aspectLoading = false;
      });
  }

  private loadIntrinsicSize(
    src: string,
    crossOrigin: string | null,
    referrerPolicy: string | null
  ): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const probe = new Image();
      if (crossOrigin !== null) {
        probe.crossOrigin = crossOrigin;
      }
      if (referrerPolicy !== null) {
        probe.referrerPolicy = referrerPolicy;
      }
      probe.decoding = "async";

      const cleanup = () => {
        probe.removeEventListener("load", onLoad);
        probe.removeEventListener("error", onError);
      };

      const onLoad = () => {
        cleanup();
        resolve({ width: probe.naturalWidth, height: probe.naturalHeight });
      };

      const onError = () => {
        cleanup();
        reject(new Error("Image failed to preload"));
      };

      probe.addEventListener("load", onLoad);
      probe.addEventListener("error", onError);
      probe.src = src;

      if (probe.complete && probe.naturalWidth > 0 && probe.naturalHeight > 0) {
        cleanup();
        resolve({ width: probe.naturalWidth, height: probe.naturalHeight });
      }
    });
  }

  private handleInView(img: HTMLImageElement, state: LazyImageState, inView: boolean): void {
    state.pendingActivation = inView;
    if (!inView) return;

    if (!state.aspectReady) {
      if (!state.aspectLoading) {
        this.prepareAspectRatio(img);
      }
      return;
    }

    this.maybeActivateImage(img, state);
  }

  private maybeActivateImage(img: HTMLImageElement, state: LazyImageState): void {
    if (!state.pendingActivation || state.contentReady || state.contentLoading) {
      return;
    }
    if (!state.aspectReady || !state.src) {
      return;
    }

    this.activateImage(img, state);
  }

  private activateImage(img: HTMLImageElement, state: LazyImageState): void {
    state.contentLoading = true;
    this.loadingCount++;

    const finalize = (loaded: boolean) => {
      if (!state.contentLoading) {
        return;
      }
      state.contentLoading = false;
      state.pendingActivation = false;
      this.loadingCount = Math.max(0, this.loadingCount - 1);

      if (loaded) {
        state.contentReady = true;
        img.classList.add("-loaded");
      }

      if (this.loadingCount === 0) {
        this.events.emit("image:load:all", null);
      }
    };

    const onLoad = () => finalize(true);
    const onError = () => {
      console.warn("Lazy image failed to load:", state.src);
      finalize(false);
    };

    img.addEventListener("load", onLoad, { once: true });
    img.addEventListener("error", onError, { once: true });
    img.src = state.src;

    if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
      img.removeEventListener("load", onLoad);
      img.removeEventListener("error", onError);
      finalize(true);
    }
  }
}
